import React, { FC } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import ControlButton from 'studio_react/common/components/VideoPlayer/ControlButton';
import { ReactComponent as ExpandIcon } from 'studio_react/svg/common/expand.svg';
import { ReactComponent as MinimizeIcon } from 'studio_react/svg/common/minimize.svg';
import { ReactComponent as PauseIcon } from 'studio_react/svg/common/pause.svg';
import { ReactComponent as PlayIcon } from 'studio_react/svg/common/play.svg';
import { ReactComponent as VolumeIcon1 } from 'studio_react/svg/common/volume-1.svg';
import { ReactComponent as VolumeIcon2 } from 'studio_react/svg/common/volume-2.svg';
import { ReactComponent as VolumeIconX } from 'studio_react/svg/common/volume-x.svg';
import { ReactComponent as VolumeIcon0 } from 'studio_react/svg/common/volume.svg';
import { useTranslate } from 'ts/common/hooks';

const formatTime = (s: number): string => {
    if (!isFinite(s) || isNaN(s)) {
        return '0:00';
    }

    const totalSeconds = Math.round(s);
    const m = Math.floor(totalSeconds / 60);
    const sec = totalSeconds % 60;

    return `${m}:${sec.toString().padStart(2, '0')}`;
};

/**
 * Diameter of the round slider thumb. The hover-reveal container around the volume slider must be
 * at least this tall: it clips with `overflow: hidden` to collapse horizontally, and a shorter box
 * crops the thumb's top and bottom, leaving a squared-off stub instead of a circle.
 */
const THUMB_SIZE = '12px';

const sliderSx = {
    appearance: 'none',
    WebkitAppearance: 'none',
    height: '4px',
    borderRadius: '2px',
    outline: 'none',
    cursor: 'pointer',
    '&::-webkit-slider-thumb': {
        WebkitAppearance: 'none',
        appearance: 'none',
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        borderRadius: '50%',
        background: 'white',
        cursor: 'pointer'
    },
    '&::-moz-range-thumb': {
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        borderRadius: '50%',
        background: 'white',
        cursor: 'pointer',
        border: 'none'
    }
};

interface SlideshowControlsProps {
    isPlaying: boolean;
    currentTime: number;
    totalDuration: number;
    isFullscreen: boolean;
    /** Music playback volume (0–1). */
    volume: number;
    isMuted: boolean;
    /**
     * Whether this slideshow has any music to govern. False hides the sound control entirely,
     * rather than offering a mute button that silences nothing.
     */
    hasAudio: boolean;
    onPlayPause: () => void;
    onSeek: (time: number) => void;
    onToggleMute: () => void;
    onVolumeChange: (value: number) => void;
    onToggleFullscreen: () => void;
}

const SlideshowControls: FC<SlideshowControlsProps> = ({
    isPlaying,
    currentTime,
    totalDuration,
    isFullscreen,
    volume,
    isMuted,
    hasAudio,
    onPlayPause,
    onSeek,
    onToggleMute,
    onVolumeChange,
    onToggleFullscreen
}) => {
    const t = useTranslate('studio.common.slideshowPlayer');
    const fillPercent =
        totalDuration > 0 ? Math.min(100, Math.max(0, (currentTime / totalDuration) * 100)) : 0;
    const volumeFillPercent = isMuted ? 0 : volume * 100;

    const VolumeIcon =
        isMuted || volume === 0
            ? VolumeIconX
            : volume <= 0.33
            ? VolumeIcon0
            : volume <= 0.66
            ? VolumeIcon1
            : VolumeIcon2;

    const FullscreenIcon = isFullscreen ? MinimizeIcon : ExpandIcon;

    return (
        <Box position="absolute" bottom={0} left={0} right={0} zIndex={2}>
            <Box
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                h="120px"
                background="linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)"
                pointerEvents="none"
                zIndex={0}
            />

            <Flex
                direction="column"
                position="relative"
                zIndex={1}
                px={{ base: '8px', md: '15px' }}
                pb={{ base: '10px', md: '17px' }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
                <Flex
                    h={{ base: '32px', md: '40px' }}
                    alignItems="center"
                    gap={{ base: '2px', md: '4px' }}
                    minW={0}
                >
                    <ControlButton
                        onClick={onPlayPause}
                        ariaLabel={isPlaying ? t('pause') : t('play')}
                        bg="rgba(0,0,0,0.6)"
                        w={{ base: '36px', md: '48px' }}
                        h={{ base: '32px', md: '40px' }}
                    >
                        {isPlaying ? (
                            <PauseIcon width={20} height={20} />
                        ) : (
                            <PlayIcon width={20} height={20} />
                        )}
                    </ControlButton>

                    <Flex
                        flex={1}
                        minW={0}
                        bg="rgba(0,0,0,0.6)"
                        px={{ base: '6px', md: '10px' }}
                        py="4px"
                        rounded="4px"
                        gap={{ base: '4px', md: '12px' }}
                        h={{ base: '32px', md: '40px' }}
                        alignItems="center"
                    >
                        <Box
                            as="input"
                            type="range"
                            min={0}
                            max={totalDuration || 1}
                            value={currentTime}
                            step={0.1}
                            aria-label={t('seek')}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                onSeek(Number(e.target.value))
                            }
                            flex={1}
                            minW={0}
                            style={{
                                background: `linear-gradient(to right, white ${fillPercent}%, rgba(255,255,255,0.3) ${fillPercent}%)`
                            }}
                            sx={sliderSx}
                        />

                        <Text color="white" fontSize="14px" whiteSpace="nowrap" flexShrink={0}>
                            {formatTime(currentTime)} / {formatTime(totalDuration)}
                        </Text>

                        {/* Sound — mute toggle plus a slider revealed on hover, mirroring the
                            video player's control. Hidden outright for a slideshow with no
                            songs on its sound timeline: there is nothing to govern. */}
                        {hasAudio && (
                            <Flex alignItems="center" gap="6px" role="group">
                                <ControlButton
                                    onClick={onToggleMute}
                                    ariaLabel={isMuted ? t('unmute') : t('mute')}
                                >
                                    <VolumeIcon width={24} height={24} />
                                </ControlButton>

                                {/* `_focusWithin` matters as much as `_groupHover`: the slider stays
                                    in the tab order while collapsed, so without it a keyboard user
                                    lands on a control clipped to zero width. */}
                                <Box
                                    w="0px"
                                    h={THUMB_SIZE}
                                    display="flex"
                                    alignItems="center"
                                    overflow="hidden"
                                    transition="width 0.2s ease"
                                    flexShrink={0}
                                    _groupHover={{ w: '72px' }}
                                    _focusWithin={{ w: '72px' }}
                                >
                                    <Box
                                        as="input"
                                        type="range"
                                        min={0}
                                        max={1}
                                        step={0.02}
                                        value={isMuted ? 0 : volume}
                                        aria-label={t('volume')}
                                        // Without this a screen reader reads the raw 0–1 value
                                        // ("0.5"); the level is meaningful to a listener as a
                                        // percentage.
                                        aria-valuetext={`${Math.round(
                                            (isMuted ? 0 : volume) * 100
                                        )}%`}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            onVolumeChange(Number(e.target.value))
                                        }
                                        w="72px"
                                        display="block"
                                        style={{
                                            background: `linear-gradient(to right, white ${volumeFillPercent}%, rgba(255,255,255,0.3) ${volumeFillPercent}%)`
                                        }}
                                        sx={sliderSx}
                                    />
                                </Box>
                            </Flex>
                        )}

                        <ControlButton
                            onClick={onToggleFullscreen}
                            ariaLabel={isFullscreen ? t('exitFullscreen') : t('fullscreen')}
                        >
                            <Box
                                w="24px"
                                h="24px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <FullscreenIcon width={20} height={20} />
                            </Box>
                        </ControlButton>
                    </Flex>
                </Flex>
            </Flex>
        </Box>
    );
};

export default SlideshowControls;
